import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { trackEvent, identifyRole } from "@/lib/analytics";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Loader2, LogIn, Eye, EyeOff, AlertCircle, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";

const BRAND_IMAGE = "https://customer-assets-39nsmqrw.emergentagent.net/job_talent-hub-822/artifacts/nu69cgy8_WhatsApp%20Image%202026-08-06%20at%2002.38.47.jpeg";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState({ email: false, password: false });

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const passwordValid = password.length >= 6;

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    if (!emailValid || !passwordValid) return;
    setLoading(true);
    setError("");
    trackEvent("login_attempt", {});
    try {
      const returnedUser = await login(email, password);
      const role = returnedUser?.role;
      trackEvent("login", { method: "password", role });
      identifyRole(role);
      toast.success("مرحباً بعودتك 👋");
      navigate(from, { replace: true });
    } catch (err) {
      trackEvent("login_failed", { reason: err?.response?.status || "unknown" });
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen-safe relative overflow-hidden"
      data-testid="login-page"
    >
      {/* Full-screen background image */}
      <img
        src={BRAND_IMAGE}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover object-center"
      />
      {/* Dark overlays — restore the branded teal/rose tint over the full screen */}
      <div className="absolute inset-0 bg-gradient-to-br from-teal-950/90 via-teal-900/75 to-rose-900/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent" />
      <div className="pointer-events-none absolute top-0 end-0 w-[600px] h-[600px] rounded-full bg-amber-400/5 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 start-0 w-[500px] h-[500px] rounded-full bg-teal-400/8 blur-3xl" />

      <div className="relative min-h-screen-safe grid lg:grid-cols-[5fr_7fr] gap-0">
        {/* Left — brand storytelling text (over the shared background) */}
        <aside className="hidden lg:flex relative flex-col justify-between p-14 xl:p-16 text-white">
          <div className="relative z-10">
            <Link to="/" className="inline-block" data-testid="login-logo">
              <Logo variant="transparent" size={120} textColor="text-white" subColor="text-white/80" />
            </Link>
          </div>

          <div className="relative z-10">
            <div className="text-xs font-bold uppercase tracking-widest text-amber-300 mb-3 drop-shadow">
              عماد — لتأهيل وتمكين القيادات الشابة
            </div>
            <h2 className="text-4xl font-heading font-extrabold leading-tight max-w-md drop-shadow-lg">
              بيئة تفاعلية متكاملة لإدارة رحلة التدريب التعاوني من التقديم حتى شهادة الإنجاز
            </h2>
            <p className="mt-4 text-teal-50 max-w-md drop-shadow">
              انضم لآلاف الطلاب والجمعيات والموجهين والممولين، وشارك في بناء جيل جاهز لسوق العمل في القطاع غير الربحي.
            </p>
          </div>

          <div className="relative z-10 text-[11px] text-white/70 drop-shadow">
            © عماد {new Date().getFullYear()} — كل الحقوق محفوظة
          </div>
        </aside>

        {/* Right — auth form */}
        <main className="flex items-center justify-center p-4 sm:p-10 lg:p-16">
          <div className="w-full max-w-md">
            {/* Mobile-only header */}
            <div className="lg:hidden flex items-center justify-between mb-8">
              <Link to="/" className="block">
                <Logo variant="transparent" size={80} textColor="text-white" subColor="text-teal-200/80" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-xs text-teal-100 hover:text-white font-medium"
                data-testid="login-back-home"
              >
                <ArrowRight className="h-4 w-4" /> الرئيسية
              </Link>
            </div>

            {/* Form card — glass on dark */}
            <div className="relative rounded-[28px] bg-white/95 backdrop-blur-xl shadow-2xl shadow-teal-950/40 border border-white/20 overflow-hidden">
              {/* Amber ribbon accent */}
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-amber-400 via-amber-300 to-rose-300" />

              <div className="p-5 sm:p-10">
                <div className="hidden lg:flex items-center justify-end mb-6">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-teal-700 font-medium transition-colors"
                    data-testid="login-back-home"
                  >
                    <ArrowRight className="h-3.5 w-3.5" /> العودة للرئيسية
                  </Link>
                </div>

                <div className="text-[11px] font-bold uppercase tracking-widest text-teal-700 mb-2">
                  دخول الأعضاء
                </div>
                <h1 className="text-2xl font-heading font-black text-slate-900 leading-tight">
                  أهلاً بك <span className="text-teal-700">مجدداً</span>
                </h1>
                <p className="mt-2 text-slate-600 text-sm">
                  أدخل بياناتك لمتابعة رحلتك في عماد.
                </p>

                <form onSubmit={submit} noValidate className="mt-7 space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">البريد الإلكتروني</label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                      placeholder="you@example.com"
                      autoComplete="email"
                      className={`w-full rounded-xl border-2 bg-slate-50/50 px-4 py-3.5 text-slate-900 focus:ring-4 outline-none transition-all ${
                        touched.email && !emailValid
                          ? "border-red-300 focus:border-red-500 focus:ring-red-500/10"
                          : "border-slate-200 focus:border-teal-600 focus:ring-teal-600/10 focus:bg-white"
                      }`}
                      data-testid="login-email-input"
                      dir="ltr"
                    />
                    {touched.email && !emailValid && (
                      <div
                        className="mt-1.5 text-xs text-red-600 flex items-center gap-1"
                        data-testid="login-email-error"
                      >
                        <AlertCircle className="h-3.5 w-3.5" /> أدخل بريداً إلكترونياً صحيحاً
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-semibold text-slate-700">كلمة المرور</label>
                      <Link
                        to="/forgot-password"
                        className="text-xs text-teal-700 font-semibold hover:underline"
                        data-testid="forgot-link"
                      >
                        نسيت كلمة المرور؟
                      </Link>
                    </div>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                        placeholder="••••••••"
                        autoComplete="current-password"
                        className={`w-full rounded-xl border-2 bg-slate-50/50 px-4 py-3.5 pe-12 text-slate-900 focus:ring-4 outline-none transition-all ${
                          touched.password && !passwordValid
                            ? "border-red-300 focus:border-red-500 focus:ring-red-500/10"
                            : "border-slate-200 focus:border-teal-600 focus:ring-teal-600/10 focus:bg-white"
                        }`}
                        data-testid="login-password-input"
                        dir="ltr"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        className="absolute inset-y-0 end-3 my-auto h-9 w-9 grid place-items-center rounded-lg text-slate-500 hover:text-teal-700 hover:bg-teal-50 transition-colors"
                        aria-label={showPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"}
                        data-testid="login-password-toggle"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {touched.password && !passwordValid && (
                      <div
                        className="mt-1.5 text-xs text-red-600 flex items-center gap-1"
                        data-testid="login-password-error"
                      >
                        <AlertCircle className="h-3.5 w-3.5" /> كلمة المرور 6 أحرف على الأقل
                      </div>
                    )}
                  </div>

                  {error && (
                    <div
                      className="p-3.5 rounded-xl bg-red-50 text-red-700 text-sm border border-red-200/70 flex items-start gap-2"
                      data-testid="login-error"
                      role="alert"
                    >
                      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-l from-teal-800 to-teal-600 text-white font-bold text-sm px-4 py-3 shadow-lg shadow-teal-900/20 hover:shadow-xl hover:shadow-teal-900/30 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none transition-all"
                    data-testid="login-submit-btn"
                  >
                    <span className="relative inline-flex items-center justify-center gap-2 w-full">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
                      تسجيل الدخول
                    </span>
                    <span className="absolute inset-y-0 -end-8 group-hover:end-4 w-8 grid place-items-center transition-all opacity-0 group-hover:opacity-100">
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </button>

                  <div className="flex items-center gap-3 py-2">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-xs text-slate-400 font-medium">أو</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>

                  <Link
                    to="/register"
                    className="block w-full text-center rounded-xl border-2 border-slate-200 bg-white hover:border-teal-600 hover:bg-teal-50/40 text-slate-800 font-bold text-sm px-6 py-3.5 transition-all"
                    data-testid="go-register-link"
                  >
                    ليس لديك حساب؟ إنشاء حساب جديد
                  </Link>
                </form>
              </div>

              <div className="px-8 sm:px-10 py-4 bg-slate-50/70 border-t border-slate-200/70 flex items-center gap-2 text-[11px] text-slate-500">
                <ShieldCheck className="h-3.5 w-3.5 text-teal-700 shrink-0" />
                جميع البيانات محمية بتشفير كامل — لا نشارك معلوماتك مع أي جهة خارجية.
              </div>
            </div>
            {/* Siaq secondary identity — «إحدى منصات سياق». Does not replace Emad. */}
            <div className="mt-6 flex flex-col items-center gap-2 opacity-90" data-testid="siaq-badge-login">
              <img
                src={require("@/assets/siaq-logo.png")}
                alt="شركة سياق"
                className="h-12 w-auto object-contain select-none pointer-events-none"
                draggable={false}
              />
              <p className="text-[11px] text-slate-500 font-medium tracking-wide">إحدى منتجات سياق</p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function TrustChip({ title, caption }) {
  return (
    <div className="rounded-xl bg-white/[0.06] backdrop-blur-md border border-white/10 px-4 py-3">
      <div className="text-sm font-bold text-white">{title}</div>
      <div className="text-[11px] text-teal-100/70 mt-0.5">{caption}</div>
    </div>
  );
}
